import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Eye, MousePointerClick, RefreshCw, AlertTriangle, ShieldOff } from 'lucide-react';
import { format } from 'date-fns';
import { getAdSessionId, resetAdFrequency } from '@/lib/adSession';

interface AdEvent {
  id: string;
  campaign_id: string;
  event_type: 'impression' | 'click';
  session_id: string | null;
  user_id: string | null;
  placement: string | null;
  page: string | null;
  created_at: string;
}

export default function AdEventsVerification() {
  const { user, hasRole, loading } = useAuth();
  const allowed = hasRole('superadmin') || hasRole('ad_operations_lead') || hasRole('ad_operations_agent');

  const [eventType, setEventType] = useState<'all' | 'impression' | 'click'>('all');
  const [placement, setPlacement] = useState<string>('all');
  const [campaignFilter, setCampaignFilter] = useState<string>('');
  const [sessionFilter, setSessionFilter] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const { data: campaigns } = useQuery({
    queryKey: ['ad-events-campaign-list'],
    queryFn: async () => {
      const { data } = await supabase.from('ad_campaigns').select('id,title,placement').order('created_at', { ascending: false });
      return data || [];
    },
    enabled: allowed,
  });

  const { data: events, isLoading, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['ad-events', eventType, placement, campaignFilter, sessionFilter],
    queryFn: async () => {
      let q = supabase.from('ad_events').select('*').order('created_at', { ascending: false }).limit(300);
      if (eventType !== 'all') q = q.eq('event_type', eventType);
      if (placement !== 'all') q = q.eq('placement', placement);
      if (campaignFilter) q = q.eq('campaign_id', campaignFilter);
      if (sessionFilter) q = q.eq('session_id', sessionFilter);
      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as AdEvent[];
    },
    enabled: allowed,
    refetchInterval: autoRefresh ? 5000 : false,
  });

  // Detect potential double-counts: same campaign + session + type within 1s
  const duplicates = useMemo(() => {
    const seen = new Map<string, AdEvent>();
    const dupes: AdEvent[] = [];
    (events || []).forEach((e) => {
      const k = `${e.campaign_id}|${e.session_id || 'none'}|${e.event_type}|${e.placement || ''}`;
      const prev = seen.get(k);
      if (prev) {
        const dt = Math.abs(new Date(prev.created_at).getTime() - new Date(e.created_at).getTime());
        if (dt < 1500) dupes.push(e);
      } else {
        seen.set(k, e);
      }
    });
    return dupes;
  }, [events]);

  const dupeIds = new Set(duplicates.map((d) => d.id));

  const campaignTitle = (id: string) =>
    campaigns?.find((c: any) => c.id === id)?.title || id.slice(0, 8);

  const totals = useMemo(() => {
    const list = events || [];
    return {
      total: list.length,
      impressions: list.filter((e) => e.event_type === 'impression').length,
      clicks: list.filter((e) => e.event_type === 'click').length,
      uniqueSessions: new Set(list.map((e) => e.session_id).filter(Boolean)).size,
    };
  }, [events]);

  const placements = ['right_sidebar', 'agencies_inline_mobile', 'agencies_city_section', 'left_sidebar', 'property_detail', 'homepage_banner'];
  const mySession = getAdSessionId();

  if (loading) {
    return <Layout><div className="container py-12"><Skeleton className="h-64" /></div></Layout>;
  }

  if (!user || !allowed) {
    return (
      <Layout>
        <div className="container py-20 text-center max-w-md mx-auto">
          <ShieldOff className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <h1 className="text-xl font-bold mb-2">Restricted</h1>
          <p className="text-sm text-muted-foreground">This page is for ad operations and superadmins only.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead title="Ad Events Verification" description="Raw ad impression and click events for QA" canonical="/dev/ad-events" />
      <div className="container py-6 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold">Ad Events Verification</h1>
            <p className="text-sm text-muted-foreground">Raw impression and click events. Refreshes automatically every 5s.</p>
            <p className="text-[11px] text-muted-foreground mt-1">Your session id: <code className="px-1 bg-muted rounded">{mySession}</code></p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setAutoRefresh((v) => !v)}>
              {autoRefresh ? 'Pause auto-refresh' : 'Resume auto-refresh'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={() => { resetAdFrequency(); }}>
              Reset my frequency cap
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total events</p><p className="text-2xl font-bold">{totals.total}</p></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3"><Eye className="h-7 w-7 text-purple-500" /><div><p className="text-2xl font-bold">{totals.impressions}</p><p className="text-xs text-muted-foreground">Impressions</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3"><MousePointerClick className="h-7 w-7 text-pink-500" /><div><p className="text-2xl font-bold">{totals.clicks}</p><p className="text-xs text-muted-foreground">Clicks</p></div></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Unique sessions</p><p className="text-2xl font-bold">{totals.uniqueSessions}</p></CardContent></Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className={`h-7 w-7 ${duplicates.length ? 'text-destructive' : 'text-emerald-500'}`} />
              <div>
                <p className="text-2xl font-bold">{duplicates.length}</p>
                <p className="text-xs text-muted-foreground">Suspected duplicates (&lt;1.5s)</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-sm">Filters</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Event type</Label>
              <Select value={eventType} onValueChange={(v: any) => setEventType(v)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="impression">Impressions</SelectItem>
                  <SelectItem value="click">Clicks</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Placement</Label>
              <Select value={placement} onValueChange={setPlacement}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {placements.map((p) => <SelectItem key={p} value={p}>{p.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Campaign</Label>
              <Select value={campaignFilter || 'all'} onValueChange={(v) => setCampaignFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All campaigns</SelectItem>
                  {(campaigns || []).map((c: any) => (
                    <SelectItem key={c.id} value={c.id} className="text-xs">{c.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Session id</Label>
              <Input className="h-9 text-xs" placeholder="paste session id…" value={sessionFilter} onChange={(e) => setSessionFilter(e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center justify-between">
              <span>Recent events (latest 300)</span>
              <span className="text-[11px] text-muted-foreground font-normal">Updated {format(new Date(dataUpdatedAt || Date.now()), 'HH:mm:ss')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {isLoading ? <Skeleton className="h-40" /> : !events?.length ? (
              <p className="text-sm text-muted-foreground text-center py-8">No events yet — interact with an ad to log one.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Time</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs">Campaign</TableHead>
                    <TableHead className="text-xs">Placement</TableHead>
                    <TableHead className="text-xs">Page</TableHead>
                    <TableHead className="text-xs">Session</TableHead>
                    <TableHead className="text-xs">User</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((e) => (
                    <TableRow key={e.id} className={dupeIds.has(e.id) ? 'bg-destructive/10' : ''}>
                      <TableCell className="text-xs whitespace-nowrap">{format(new Date(e.created_at), 'HH:mm:ss.SSS')}</TableCell>
                      <TableCell>
                        <Badge variant={e.event_type === 'click' ? 'default' : 'secondary'} className="text-[10px]">
                          {e.event_type}
                        </Badge>
                        {dupeIds.has(e.id) && <Badge variant="destructive" className="text-[10px] ml-1">dup</Badge>}
                      </TableCell>
                      <TableCell className="text-xs max-w-[200px] truncate" title={e.campaign_id}>{campaignTitle(e.campaign_id)}</TableCell>
                      <TableCell className="text-xs">{e.placement || '—'}</TableCell>
                      <TableCell className="text-xs">{e.page || '—'}</TableCell>
                      <TableCell className="text-[10px] font-mono max-w-[140px] truncate" title={e.session_id || ''}>{e.session_id || '—'}</TableCell>
                      <TableCell className="text-[10px] font-mono">{e.user_id ? e.user_id.slice(0, 8) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
